import React, { useMemo, useCallback } from 'react';
import { Table as TableComponent, Button } from '@lucid/lego';
import type { Prospect } from '../../../../api/services/lupp/prospect/dto.prospect';
import { createColumns } from './utils';
import Details from '../Details/Details';
import styles from './utils.module.scss';

interface TableProps {
  theme: 'light' | 'dark';
  data: Prospect[];
  totalCount: number;
  currentPage: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  labels: Record<string, string>;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onCreateQuotation: (prospectId?: string) => void;
  onCreateProspect: () => void;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  selectedProspectId?: string | null;
  onProspectSelect?: (prospectId: string | null) => void;
  /** Active tab ID from URL hash */
  activeTab?: string;
  /** Callback when tab changes */
  onTabChange?: (tabId: string) => void;
}

const Table: React.FC<TableProps> = ({
  theme,
  data,
  totalCount,
  currentPage,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  labels,
  searchQuery,
  onSearchChange,
  onCreateQuotation,
  onCreateProspect,
  sortColumn,
  sortDirection,
  onSort,
  selectedProspectId,
  onProspectSelect,
  activeTab = 'details',
  onTabChange,
}) => {
  // Slider is open when there's a selectedProspectId in URL
  const isSliderOpen = !!selectedProspectId;

  const handleEditClick = useCallback(
    (record: Prospect) => {
      onProspectSelect?.(record.prospectIdentifier || null);
    },
    [onProspectSelect]
  );

  const handleClose = useCallback(() => {
    onProspectSelect?.(null);
  }, [onProspectSelect]);

  const columns = useMemo(
    () => createColumns({ theme, onEditClick: handleEditClick, labels }),
    [theme, labels, handleEditClick]
  );

  return (
    <>
      <TableComponent
        className={styles.tableWrapper}
        columns={columns}
        data={data}
        theme={theme === 'light' ? 'dark' : 'light'}
        selection={{ enabled: false }}
        pagination={{
          enabled: true,
          total: totalCount,
          current: currentPage,
          pageSize,
          onChange: (page) => onPageChange(page),
          onPageSizeChange,
        }}
        search={{
          placeholder: 'Search by name, email, phone, status, source...',
          value: searchQuery,
          onChange: onSearchChange,
        }}
        sort={{
          column: sortColumn,
          direction: sortDirection,
          onSort: onSort,
        }}
        columnConfig={{ enabled: true }}
        toolbar={{
          content: (
            <Button
              type="tertiary"
              color="sapphire"
              size="small"
              theme="light"
              label={labels?.new_prospect || 'New Prospect'}
              iconLeft={{ category: 'global', name: 'circle-plus' }}
              handleClick={onCreateProspect}
              gaComponent="prospects-new-prospect"
              dataTestId="new-prospect-button"
            />
          ),
        }}
        emptyState={{
          message: searchQuery.trim()
            ? 'No results found! Please update your query'
            : 'No prospects available',
        }}
        gaComponent="prospects-pipeline-table"
        dataTestId="prospects-pipeline-table"
      />
      <Details
        theme={theme}
        isOpen={isSliderOpen}
        onClose={handleClose}
        prospectId={selectedProspectId || null}
        labels={labels}
        onCreateQuotation={() => {
          onProspectSelect?.(null);
          onCreateQuotation(selectedProspectId || undefined);
        }}
        activeTab={activeTab}
        onTabChange={onTabChange}
      />
    </>
  );
};

export default Table;
